import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { FiMessageSquare, FiRefreshCw } from 'react-icons/fi';
import KpiBar from '../components/dashboard/KpiBar';
import { ErrorState } from '../components/dashboard/states';
import SmsOutreachService, { DISPATCH_DAG_ID, PLANNER_DAG_ID } from '../services/SmsOutreachService';
import { isFullAdmin } from '../utils/adminIds';
import PipelineStatusCard from '../components/smsOutreach/PipelineStatusCard';
import ConfirmRunModal from '../components/smsOutreach/ConfirmRunModal';
import RunsTable from '../components/smsOutreach/RunsTable';
import RunDetailSheet from '../components/smsOutreach/RunDetailSheet';
import MessagesTable from '../components/smsOutreach/MessagesTable';
import UpcomingTable from '../components/smsOutreach/UpcomingTable';
import Badge from '../components/smsOutreach/Badge';
import {
  ConversionChart,
  DailyVolumeChart,
  ExperimentCard,
  FailureCategoriesChart,
  FunnelChart,
  SegmentsChart,
} from '../components/smsOutreach/OutreachCharts';
import { FAILURE_CATEGORY_LABELS, fmtDateTime, fmtMoney, fmtNumber, fmtPct, pctDelta, relativeTime, toIsoDay } from '../components/smsOutreach/format';

const RANGES = [
  { id: '1d', label: '1D', days: 1 },
  { id: '7d', label: '7D', days: 7 },
  { id: '14d', label: '14D', days: 14 },
  { id: '30d', label: '30D', days: 30 },
  { id: '90d', label: '90D', days: 90 },
];
const MODES = [
  { id: 'live', label: 'Live' },
  { id: 'dry_run', label: 'Dry run' },
  { id: 'all', label: 'All' },
];
const TABS = [
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'runs', label: 'Runs' },
  { id: 'messages', label: 'Messages' },
  { id: 'failures', label: 'Failures' },
];

// Which Airflow DAG a status card / confirm dialog is about.
const DAGS = {
  planner: PLANNER_DAG_ID,
  dispatcher: DISPATCH_DAG_ID,
};

const STATUS_POLL_IDLE_MS = 30000;
const STATUS_POLL_ACTIVE_MS = 8000;

function rangeFor(id) {
  const opt = RANGES.find((r) => r.id === id) || RANGES.find((r) => r.id === '30d') || RANGES[0];
  const end = new Date();
  const start = new Date(end.getTime() - (opt.days - 1) * 86400000);
  const label = opt.days === 1 ? 'Today' : `Last ${opt.days} days`;
  return { from: toIsoDay(start), to: toIsoDay(end), label };
}

/**
 * SMS Outreach: oversight of the automated guest pre-arrival SMS pipeline.
 * Pipeline status + controls (Airflow), KPIs and charts (Mongo analytics),
 * and drill-down tables for runs, individual messages and failures.
 */
export default function SmsOutreachScreen() {
  const { adminInfo } = useSelector((state) => state.login);
  const fullAdmin = isFullAdmin(adminInfo);

  const [rangeId, setRangeId] = useState('30d');
  const [mode, setMode] = useState('live');
  const [tab, setTab] = useState('upcoming');
  const range = useMemo(() => rangeFor(rangeId), [rangeId]);
  const params = useMemo(() => ({ from: range.from, to: range.to, mode }), [range, mode]);

  // Airflow status: planner (guest_sms_daily) + dispatcher (guest_sms_dispatch)
  const [status, setStatus] = useState(null);
  const [dispatchStatus, setDispatchStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [statusRefreshing, setStatusRefreshing] = useState(false);
  const statusTimer = useRef(null);

  const unavailable = (e) => ({
    airflow: { available: false, reason: e.response?.status ? `http_${e.response.status}` : 'unreachable' },
    canOperate: false,
  });

  const loadStatus = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setStatusRefreshing(true);
    const [planner, dispatcher] = await Promise.all([
      SmsOutreachService.airflowStatus().catch(unavailable),
      SmsOutreachService.airflowStatus(DISPATCH_DAG_ID).catch(unavailable),
    ]);
    setStatus(planner);
    setDispatchStatus(dispatcher);
    setStatusLoading(false);
    setStatusRefreshing(false);
  }, []);

  useEffect(() => {
    loadStatus({ silent: true });
  }, [loadStatus]);

  const anyActive = Boolean(status?.airflow?.activeRun || dispatchStatus?.airflow?.activeRun);
  useEffect(() => {
    clearInterval(statusTimer.current);
    statusTimer.current = setInterval(() => loadStatus({ silent: true }), anyActive ? STATUS_POLL_ACTIVE_MS : STATUS_POLL_IDLE_MS);
    return () => clearInterval(statusTimer.current);
  }, [anyActive, loadStatus]);

  // Analytics
  const [summary, setSummary] = useState(null);
  const [daily, setDaily] = useState([]);
  const [failures, setFailures] = useState(null);
  const [conversion, setConversion] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [conversionLoading, setConversionLoading] = useState(true);
  const [error, setError] = useState('');
  const analyticsReq = useRef(0);

  const loadAnalytics = useCallback(async () => {
    const id = ++analyticsReq.current;
    setLoading(true);
    setConversionLoading(true);
    setError('');
    try {
      const [s, d, f, r] = await Promise.all([
        SmsOutreachService.summary(params),
        SmsOutreachService.daily(params),
        SmsOutreachService.failures({ ...params, limit: 100 }),
        SmsOutreachService.runs({ limit: 100, mode: mode === 'all' ? 'all' : mode }),
      ]);
      if (id !== analyticsReq.current) return;
      setSummary(s);
      setDaily(d?.days || []);
      setFailures(f);
      setRuns(r);
    } catch (e) {
      if (id !== analyticsReq.current) return;
      setError(e.response?.data?.error || e.message || 'Failed to load analytics');
    } finally {
      if (id === analyticsReq.current) setLoading(false);
    }
    // Conversion joins orders and is slower; load it separately so the page paints first.
    try {
      const c = await SmsOutreachService.conversion(params);
      if (id === analyticsReq.current) setConversion(c);
    } catch (_) {
      if (id === analyticsReq.current) setConversion(null);
    } finally {
      if (id === analyticsReq.current) setConversionLoading(false);
    }
  }, [params, mode]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  // When a run finishes (activeRun goes from set → null) refresh analytics + the queue.
  const [upcomingKey, setUpcomingKey] = useState(0);
  const prevActive = useRef(null);
  useEffect(() => {
    const active = `${status?.airflow?.activeRun?.runId || ''}|${dispatchStatus?.airflow?.activeRun?.runId || ''}`;
    if (prevActive.current && prevActive.current !== '|' && active === '|') {
      loadAnalytics();
      setUpcomingKey((k) => k + 1);
    }
    prevActive.current = active;
  }, [status?.airflow?.activeRun, dispatchStatus?.airflow?.activeRun, loadAnalytics]);

  // Operate
  const [confirmKind, setConfirmKind] = useState(null);
  const [confirmDag, setConfirmDag] = useState('planner');
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmError, setConfirmError] = useState('');
  const [toast, setToast] = useState(null);

  const showToast = (text, tone = 'emerald') => {
    setToast({ text, tone });
    setTimeout(() => setToast(null), 6000);
  };

  const executeConfirm = async () => {
    setConfirmBusy(true);
    setConfirmError('');
    const dagId = DAGS[confirmDag] || PLANNER_DAG_ID;
    const dagLabel = confirmDag === 'dispatcher' ? 'Dispatcher' : 'Planner';
    try {
      if (confirmKind === 'pause' || confirmKind === 'resume') {
        const res = await SmsOutreachService.airflowPause(confirmKind === 'pause', dagId);
        showToast(res.isPaused ? `${dagLabel} paused.` : `${dagLabel} resumed.`, res.isPaused ? 'amber' : 'emerald');
      } else {
        const res = await SmsOutreachService.airflowTrigger({ mode: confirmKind, dagId });
        showToast(`${dagLabel} ${confirmKind === 'live' ? 'LIVE' : 'dry'} run queued: ${res.run?.runId || ''}`, confirmKind === 'live' ? 'rose' : 'indigo');
      }
      setConfirmKind(null);
      await loadStatus();
    } catch (e) {
      setConfirmError(e.response?.data?.error || e.message || 'Request failed');
    } finally {
      setConfirmBusy(false);
    }
  };

  const [selectedRunId, setSelectedRunId] = useState(null);
  const [messagesRunId, setMessagesRunId] = useState(null);

  const canOperate = fullAdmin && Boolean(status?.canOperate || dispatchStatus?.canOperate);

  const totals = useMemo(() => summary?.totals || {}, [summary]);
  const previous = useMemo(() => summary?.previous || {}, [summary]);
  const kpis = useMemo(
    () => [
      { id: 'sent', label: 'Sent', value: fmtNumber(totals.sent), delta: pctDelta(totals.sent, previous.sent), hint: 'Accepted by Vonage in range' },
      { id: 'guests', label: 'Guests reached', value: fmtNumber(totals.uniquePhones), delta: pctDelta(totals.uniquePhones, previous.uniquePhones), hint: 'Unique phone numbers' },
      {
        id: 'success',
        label: 'Success rate',
        value: fmtPct(totals.successRatePct),
        delta: pctDelta(totals.successRatePct, previous.successRatePct),
        hint: 'sent ÷ attempted',
      },
      { id: 'failed', label: 'Failed', value: fmtNumber(totals.failed), delta: pctDelta(totals.failed, previous.failed), invertDelta: true, hint: 'Rejected by Vonage or pipeline' },
      {
        id: 'conversion',
        label: 'Conversion',
        value: conversionLoading ? '…' : fmtPct(conversion?.conversionPct),
        sublabel: conversionLoading ? 'computing' : `${fmtNumber(conversion?.converted)} orders · ${fmtMoney(conversion?.revenue)}`,
        hint: 'Texted guests who ordered for that stay',
      },
      { id: 'replies', label: 'Replies', value: fmtNumber(totals.replies), delta: pctDelta(totals.replies, previous.replies), hint: 'Inbound SMS in range' },
      { id: 'optouts', label: 'Opt-outs', value: fmtNumber(totals.optOuts), delta: pctDelta(totals.optOuts, previous.optOuts), invertDelta: true, hint: `${fmtPct(totals.optOutRatePct)} of sent` },
      { id: 'segments', label: 'Segments', value: fmtNumber(totals.segments), delta: pctDelta(totals.segments, previous.segments), invertDelta: true, hint: 'Carrier billing units' },
      { id: 'runs', label: 'Runs', value: fmtNumber(summary?.runs?.total), sublabel: summary?.runs ? `${summary.runs.failed} failed` : '', hint: 'Pipeline executions in range' },
      {
        id: 'lastRun',
        label: 'Last run',
        value: summary?.lastRun ? relativeTime(summary.lastRun.startedAt) : '—',
        sublabel: summary?.lastRun
          ? `${fmtDateTime(summary.lastRun.startedAt)} · ${summary.lastRun.status} · ${summary.lastRun.mode === 'live' ? 'LIVE' : 'dry'}`
          : 'none recorded',
        hint: 'Most recent pipeline run',
      },
    ],
    [totals, previous, summary, conversion, conversionLoading]
  );

  const onKpiSelect = (m) => {
    if (m.id === 'failed') setTab('failures');
    else if (m.id === 'conversion') setTab('upcoming');
    else if (m.id === 'runs' || m.id === 'lastRun') setTab('runs');
    else if (m.id === 'sent' || m.id === 'guests' || m.id === 'segments') setTab('messages');
  };

  return (
    <div className="flex-1 overflow-y-auto bg-zinc-50">
      <div className="mx-auto max-w-[1600px] px-6 py-6">
        <div className="mb-5 flex flex-col gap-3 laptop:flex-row laptop:items-end laptop:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-zinc-900 text-white shadow-sm">
              <FiMessageSquare className="h-5 w-5" />
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-indigo-600">Guest engagement</p>
              <h1 className="text-2xl font-bold tracking-tight text-zinc-900">SMS Outreach</h1>
              <p className="text-sm text-zinc-500">Pre-arrival texts to upcoming guests — planner, dispatcher, queue, volume, failures and conversion.</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center rounded-lg bg-zinc-100 p-0.5">
              {RANGES.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setRangeId(r.id)}
                  className={`h-8 rounded-md px-2.5 text-xs font-medium transition-colors ${rangeId === r.id ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-600 hover:text-zinc-900'}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <div className="flex items-center rounded-lg bg-zinc-100 p-0.5">
              {MODES.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setMode(m.id)}
                  className={`h-8 rounded-md px-2.5 text-xs font-medium transition-colors ${mode === m.id ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-600 hover:text-zinc-900'}`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => {
                loadAnalytics();
                loadStatus();
              }}
              disabled={loading}
              className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3 text-xs font-semibold text-zinc-700 shadow-sm hover:bg-zinc-50 disabled:opacity-50"
            >
              <FiRefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        <div className="space-y-4">
          <div className="grid gap-4 desktop:grid-cols-2">
            <PipelineStatusCard
              variant="planner"
              status={status}
              loading={statusLoading}
              refreshing={statusRefreshing}
              canOperate={canOperate}
              onRefresh={() => loadStatus()}
              onRequest={(kind) => {
                setConfirmError('');
                setConfirmDag('planner');
                setConfirmKind(kind);
              }}
            />
            <PipelineStatusCard
              variant="dispatcher"
              status={dispatchStatus}
              loading={statusLoading}
              refreshing={statusRefreshing}
              canOperate={canOperate}
              onRefresh={() => loadStatus()}
              onRequest={(kind) => {
                setConfirmError('');
                setConfirmDag('dispatcher');
                setConfirmKind(kind);
              }}
            />
          </div>

          {error ? (
            <div className="rounded-2xl border border-zinc-200 bg-white">
              <ErrorState title="Analytics unavailable" description={error} onRetry={loadAnalytics} />
            </div>
          ) : (
            <>
              <KpiBar metrics={kpis} onSelect={onKpiSelect} loading={loading} />

              <div className="grid gap-4 desktop:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
                <DailyVolumeChart days={daily} loading={loading} mode={mode} />
                <FunnelChart funnel={summary?.funnel} loading={loading} />
              </div>
              <div className="grid gap-4 desktop:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
                <ConversionChart conversion={conversion} loading={conversionLoading} />
                <FailureCategoriesChart failures={failures} loading={loading} />
              </div>
              <div className="grid gap-4 desktop:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                <ExperimentCard conversion={conversion} loading={conversionLoading} />
                <SegmentsChart days={daily} loading={loading} />
              </div>

              <section className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center rounded-lg bg-zinc-100 p-0.5">
                    {TABS.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setTab(t.id)}
                        className={`h-8 rounded-md px-3 text-xs font-medium transition-colors ${tab === t.id ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-600 hover:text-zinc-900'}`}
                      >
                        {t.label}
                        {t.id === 'failures' && totals.failed ? <span className="ml-1.5 rounded bg-rose-50 px-1 text-[10px] font-semibold text-rose-700">{fmtNumber(totals.failed)}</span> : null}
                      </button>
                    ))}
                  </div>
                  <p className="text-[11px] text-zinc-500">
                    {range.label} · {MODES.find((m) => m.id === mode)?.label}
                    {!fullAdmin ? ' · phone numbers masked' : ''}
                  </p>
                </div>

                {tab === 'upcoming' ? (
                  <div className="h-[560px]">
                    <UpcomingTable canOperate={canOperate} refreshKey={upcomingKey} onCancelled={() => showToast('Text cancelled.', 'amber')} />
                  </div>
                ) : null}
                {tab === 'runs' ? (
                  <RunsTable
                    runs={runs}
                    loading={loading}
                    onSelect={(r) => setSelectedRunId(r.runId)}
                  />
                ) : null}
                {tab === 'messages' ? (
                  <div className="h-[560px]">
                    <MessagesTable range={range} mode={mode} runId={messagesRunId} onClearRun={() => setMessagesRunId(null)} />
                  </div>
                ) : null}
                {tab === 'failures' ? <FailuresPanel failures={failures} loading={loading} onOpenRun={setSelectedRunId} /> : null}
              </section>
            </>
          )}
        </div>
      </div>

      <ConfirmRunModal
        kind={confirmKind}
        dag={confirmDag}
        busy={confirmBusy}
        error={confirmError}
        onCancel={() => !confirmBusy && setConfirmKind(null)}
        onConfirm={executeConfirm}
      />

      {selectedRunId ? (
        <RunDetailSheet
          runId={selectedRunId}
          airflowUiBase={status?.airflow?.uiUrl ? status.airflow.uiUrl.split('/dags/')[0] : null}
          onClose={() => setSelectedRunId(null)}
        />
      ) : null}

      {toast ? (
        <div className="pointer-events-none fixed bottom-5 left-1/2 z-50 -translate-x-1/2">
          <Badge tone={toast.tone} className="px-3 py-1.5 text-[12px] shadow-lg">
            {toast.text}
          </Badge>
        </div>
      ) : null}
    </div>
  );
}

function FailuresPanel({ failures, loading, onOpenRun }) {
  const recent = failures?.recent || [];
  if (loading) return <div className="h-[420px] animate-pulse rounded-lg border border-zinc-200 bg-zinc-50" />;
  return (
    <div className="h-[420px] overflow-auto rounded-lg border border-zinc-200">
      <table className="min-w-full text-left text-xs">
        <thead className="sticky top-0 z-10 bg-zinc-50 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
          <tr>
            <th className="px-3 py-2">Run (PT)</th>
            <th className="px-3 py-2">Mode</th>
            <th className="px-3 py-2">Row</th>
            <th className="px-3 py-2">Phone</th>
            <th className="px-3 py-2">CID</th>
            <th className="px-3 py-2">Check-in</th>
            <th className="px-3 py-2">Category</th>
            <th className="px-3 py-2">Reason</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100 bg-white">
          {recent.length === 0 ? (
            <tr>
              <td colSpan={8} className="px-3 py-10 text-center text-xs text-zinc-500">
                No row-level failures recorded in range. Provider-side rejections show in the “Why sends fail” chart and under Messages → Failed.
              </td>
            </tr>
          ) : (
            recent.map((f, i) => (
              <tr key={`${f.runId}-${f.row}-${i}`} className="cursor-pointer hover:bg-zinc-50" onClick={() => onOpenRun(f.runId)}>
                <td className="whitespace-nowrap px-3 py-2">
                  <div className="text-zinc-800">{fmtDateTime(f.startedAt)}</div>
                  <div className="font-mono text-[10px] text-zinc-400">{f.runId}</div>
                </td>
                <td className="px-3 py-2">
                  <Badge tone={f.mode === 'live' ? 'rose' : 'sky'}>{f.mode === 'live' ? 'LIVE' : 'dry'}</Badge>
                </td>
                <td className="px-3 py-2 tabular-nums text-zinc-500">{f.row}</td>
                <td className="px-3 py-2 font-mono text-zinc-700">{f.phoneMasked || '—'}</td>
                <td className="px-3 py-2 font-mono text-zinc-700">{f.confirmationId || '—'}</td>
                <td className="px-3 py-2 text-zinc-700">{f.checkInDate || '—'}</td>
                <td className="px-3 py-2">
                  <Badge tone="rose">{FAILURE_CATEGORY_LABELS[f.category] || f.category}</Badge>
                </td>
                <td className="max-w-[480px] truncate px-3 py-2 text-zinc-600" title={f.reason}>
                  {f.reason}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
